/**
 * How one matrix cell should render (doc 10).
 *
 * Pure and separately tested: this is the piece where "never configured",
 * "revoked", "expired" and "not started yet" are easy to conflate, and
 * conflating them is exactly what makes a licensing screen untrustworthy.
 */
import type { AppSummary, LicenseCellState } from '@/types/api';

export type CellVariant =
  | 'unconfigured'
  | 'enabled'
  | 'disabled'
  | 'expired'
  | 'pending'
  | 'app-disabled';

export interface CellState {
  variant: CellVariant;
  /**
   * Where the switch sits. An expired license is still "on" — it just grants
   * nothing — and showing it as off would invite someone to "fix" it by
   * toggling, which would change nothing.
   */
  checked: boolean;
  interactive: boolean;
  tooltip: string;
}

const DAY = 24 * 60 * 60 * 1000;
export const EXPIRY_WARNING_DAYS = 30;

export function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const time = Date.parse(iso);
  if (Number.isNaN(time)) return '—';
  return new Intl.DateTimeFormat('de-CH', { dateStyle: 'medium' }).format(new Date(time));
}

export function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  const time = Date.parse(iso);
  if (Number.isNaN(time)) return '—';
  return new Intl.DateTimeFormat('de-CH', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(time));
}

export function daysUntil(iso: string | null, now: number = Date.now()): number | null {
  if (!iso) return null;
  const time = Date.parse(iso);
  return Number.isNaN(time) ? null : Math.ceil((time - now) / DAY);
}

export function isExpiringSoon(cell: LicenseCellState, now: number = Date.now()): boolean {
  if (!cell || !cell.enabled || !cell.isActive) return false;
  const days = daysUntil(cell.validUntil, now);
  return days !== null && days >= 0 && days <= EXPIRY_WARNING_DAYS;
}

export function describeCell(
  app: AppSummary,
  cell: LicenseCellState,
  now: number = Date.now(),
): CellState {
  if (!app.isEnabled) {
    return {
      variant: 'app-disabled',
      checked: false,
      interactive: false,
      tooltip: 'App ist noch nicht freigegeben.',
    };
  }

  if (cell === null) {
    return {
      variant: 'unconfigured',
      checked: false,
      interactive: true,
      tooltip: 'Noch nie konfiguriert — kein Zugriff.',
    };
  }

  if (!cell.enabled) {
    const by = cell.updatedBy ? ` von ${cell.updatedBy}` : '';
    return {
      variant: 'disabled',
      checked: false,
      interactive: true,
      tooltip: `Zugriff entzogen${by}.`,
    };
  }

  if (cell.reason === 'license_expired') {
    return {
      variant: 'expired',
      checked: true,
      interactive: true,
      tooltip: `Lizenz abgelaufen am ${formatDate(cell.validUntil)} — kein Zugriff.`,
    };
  }

  if (cell.reason === 'license_not_started') {
    return {
      variant: 'pending',
      checked: true,
      interactive: true,
      tooltip: `Lizenz gilt erst ab ${formatDate(cell.validFrom)}.`,
    };
  }

  const days = daysUntil(cell.validUntil, now);
  const expiry =
    days !== null && days <= EXPIRY_WARNING_DAYS
      ? ` Läuft in ${days} Tag(en) ab.`
      : cell.validUntil
        ? ` Gültig bis ${formatDate(cell.validUntil)}.`
        : '';

  return {
    variant: 'enabled',
    checked: true,
    interactive: true,
    tooltip: `Zugriff erteilt.${expiry}`,
  };
}
